/**
 * The public lists' email links: what each landing page asks, which API
 * operation its button calls, and what it says afterwards.
 *
 * Every page waits for a click — see community-list-link.js.
 */

/** @type {Record<string, string>} */
export const LIST_NAMES = {
    'early-access': 'early access',
    newsletter: 'the newsletter',
};

/**
 * @typedef {object} ListLink
 * @property {string} title    the card heading
 * @property {string} prompt   what the page asks before the click
 * @property {string} button   the button's label
 * @property {'POST'|'DELETE'} method
 * @property {string} path     the API operation the button calls
 */

/**
 * What a /lists/:list/:id/:action page does, or null for one that does not
 * exist.
 *
 * @param {string|undefined} list
 * @param {string|undefined} id
 * @param {string|undefined} action
 * @returns {ListLink|null}
 */
export function listLink(list, id, action) {
    if (!list || !id || !Object.hasOwn(LIST_NAMES, list)) return null;
    const entry = `/lists/${encodeURIComponent(list)}/${encodeURIComponent(id)}`;

    if (action === 'confirm') {
        return {
            title: 'Confirm your sign-up',
            prompt: 'Confirm this is your email address?',
            button: 'Confirm',
            method: 'POST',
            path: `${entry}/confirm`,
        };
    }
    if (action === 'leave' && list === 'early-access') {
        return {
            title: 'Leave early access',
            prompt: "Leave the early-access list? You'll lose your place in the beta queue and won't hear when early access opens. This doesn't change the newsletter.",
            button: 'Leave early access',
            method: 'DELETE',
            path: entry,
        };
    }
    if (action === 'leave' && list === 'newsletter') {
        return {
            title: 'Unsubscribe from the newsletter',
            prompt: "Stop the monthly Dice Driven newsletter? This doesn't change your place in the beta queue.",
            button: 'Unsubscribe',
            method: 'DELETE',
            path: entry,
        };
    }
    return null;
}

/**
 * What the page says once a confirm link has done its work.
 *
 * @param {string[]} lists  the lists the address is now confirmed on
 * @returns {string}
 */
export function confirmedText(lists) {
    const earlyAccess = lists.includes('early-access');
    const newsletter = lists.includes('newsletter');
    if (earlyAccess && newsletter) {
        return "You're confirmed. You have a place in the beta queue, and you'll get the monthly newsletter. We invite people in small waves, so your invite may take a while.";
    }
    if (earlyAccess) {
        return "You're confirmed. You have a place in the beta queue. We invite people in small waves, so your invite may take a while.";
    }
    if (newsletter) {
        return "You're confirmed. You'll get the monthly Dice Driven newsletter, starting with the next issue.";
    }
    return "You're confirmed.";
}

/**
 * What the page says once a leave link has done its work.
 *
 * @param {string} list
 * @returns {string}
 */
export function leftText(list) {
    return list === 'newsletter'
        ? "Done. You won't get the newsletter any more."
        : "Done. You're off the early-access list.";
}
